import { useId, useState } from "react";
import "./ExpandableText.css";

/*
  ExpandableText - long descriptions stay compact.

  Collapsed, the text is cut with "..." after `lines` lines; clicking it (or
  pressing Enter / Space on it) shows the whole text, and clicking again folds it.
  Short text is rendered as it is and is not interactive. It follows the text's own
  direction (dir="auto") so Arabic and English both read correctly, wraps inside its
  container (no horizontal overflow) and uses the theme's colours.

  text       the content
  lines      lines shown when collapsed (default 2)
  threshold  texts up to this many characters are never folded (default 90)
  labels     { expand, collapse } accessible hints
*/
export default function ExpandableText({ text, lines = 2, threshold = 90, labels = {}, as: Tag = "p", className = "" }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const value = typeof text === "string" ? text : "";
  if (!value) return null;
  if (value.length <= threshold) return <Tag className={`ui-expandable ${className}`} dir="auto">{value}</Tag>;

  const toggle = () => setOpen((current) => !current);
  return (
    <Tag
      id={id}
      className={`ui-expandable is-long ${open ? "is-open" : "is-collapsed"} ${className}`}
      dir="auto"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      title={open ? labels.collapse : labels.expand}
      style={open ? undefined : { WebkitLineClamp: lines }}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
    >
      {value}
    </Tag>
  );
}
